import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useDeleteEntityDocumentMutation,
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Dropzone, SelectButtons, getSelectValueByEnum } from 'ui';

export interface JointlyDocument {
  data: string;
  type: string;
}

export enum JointlyDocType {
  JointlyDriverLicense = 'jointly_driver_license',
  JointlyPassport = 'jointly_passport',
}

export const validationSchemaJointlyIdentificationDetails = yup.object({
  documents: yup.array().of(
    yup.object<JointlyDocument>({
      data: yup.string().trim().required(),
      type: yup.string().trim().required(),
    }),
  ),
});

const initialValues: JointlyIdentificationDocsFormik = {
  documents: [],
};

export interface JointlyIdentificationDocsFormik {
  documents: JointlyDocument[];
}

export const documentOptionsJointly = [
  {
    label: 'Driver Licence',
    value: JointlyDocType.JointlyDriverLicense,
  },
  {
    label: 'Passport',
    value: JointlyDocType.JointlyPassport,
  },
];

export const JointlyIdentificationDetails = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));
  const [docType, setDocType] = useState<JointlyDocType>(JointlyDocType.JointlyPassport);
  const [isFileLoading, setIsFileLoading] = useState(false);

  const currentDocuments = useMemo(() => {
    const documents = (data?.entity?.document_files || []).filter(
      item =>
        item.type === JointlyDocType.JointlyPassport ||
        item.type?.includes(JointlyDocType.JointlyDriverLicense),
    );

    if (documents?.[0]?.type) {
      if (documents?.[0]?.type?.includes(JointlyDocType.JointlyDriverLicense)) {
        setDocType(JointlyDocType.JointlyDriverLicense);
      } else {
        setDocType(documents?.[0]?.type as JointlyDocType);
      }
    }

    return documents;
  }, [data?.entity?.document_files]);

  const formik = useFormik<JointlyIdentificationDocsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaJointlyIdentificationDetails,
    initialValues,
    onSubmit: async values => {
      const currentDocuments = (data?.entity?.document_files || []).filter(
        item =>
          item.type === JointlyDocType.JointlyPassport ||
          item.type === JointlyDocType.JointlyDriverLicense,
      );
      if (currentDocuments.length < 1 && values.documents.length < 1) {
        formik.setFieldError('documents', 'Please upload document');
        return;
      }
      const documents =
        values?.documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')) ?? [];
      if (documents?.length > 0) {
        await updateEntity({
          ...data?.entity,
          documents,
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const { mutateAsync: deleteDocument, isLoading: isDocumentDeletion } =
    useDeleteEntityDocumentMutation({});

  const handleChangeType = (docType: JointlyDocType) => {
    setDocType(docType || JointlyDocType.JointlyPassport);
  };

  const onFileAccepted = async (file: File) => {
    setIsFileLoading(true);

    const base64 = await fileToBase64(file, true);

    await formik.setFieldValue('documents', [
      ...formik.values.documents,
      {
        type: docType,
        data: base64,
      },
    ]);
    formik.setFieldError('documents', '');

    setIsFileLoading(false);
  };

  const onFileClear = async () => {
    const current = formik.values.documents.find(doc => doc.type === docType);
    if (current?.data?.toLowerCase()?.startsWith('http')) {
      await deleteDocument({ entity_id: data?.entity.id || 0, type: docType });
    }
    await formik.setFieldValue(
      'documents',
      formik.values.documents.filter(doc => doc.type !== docType),
    );
    formik.setFieldError('documents', '');
  };

  useEffect(() => {
    formik.setFieldValue(
      'documents',
      currentDocuments.map<JointlyDocument>(item => ({
        data: item.url as string,
        type: item.type as string,
      })),
    );
  }, [currentDocuments]);

  return (
    <OnboardingWrapper
      title="Jointly identification documents"
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      formik={formik}
      isLoading={isLoading}
    >
      <>
        <SelectButtons
          options={documentOptionsJointly}
          selected={getSelectValueByEnum<JointlyDocType>(docType)}
          onChange={handleChangeType}
        />
        <div className="w-full flex flex-col items-start justify-center space-y-4">
          <div className="w-full flex flex-col space-y-1">
            <p className="text-gray-900 text-[14px] leading-[22px]">Upload image</p>
            <p className="text-gray-600 text-[12px] leading-[20px]">JPG, PNG or PDF. Up to 10 mb</p>
          </div>
          <Dropzone
            onFileAccepted={onFileAccepted}
            loading={isFileLoading || isDocumentDeletion}
            value={formik.values.documents?.find(item => item.type === docType)?.data}
            onFileClear={onFileClear}
            error={formik.errors.documents as string}
          />
        </div>
      </>
    </OnboardingWrapper>
  );
};
